// useStore Info WIP
import { useQuery as useMarketingAmountRangesQuery } from "@faire/web-api/api/launch-with-faire/marketing-amount-ranges/get-hooks";
import { IRetailer } from "@faire/web-api/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api/indigofair/data/IRetailerLaunchWithFaireApplication";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotNull } from "@faire/web/common/typescriptUtils";

import {
  BestStoreType,
  getBrickAndMortarMonthlyRentText,
  DefaultRequiredStoreFieldKeys,
  LeaseLengthOptions,
  LeaseSignedOptions,
  getNonBMMonthlyRentText,
  useOnlineHasStoreWebsiteOptions,
  OnlineStoreCommitment,
  OwFFormFieldKey,
  OwFFormFieldType,
  PopUpDaysPerWeek,
  PopUpHoursOptions,
  PopUpLeaseOptions,
  PopUpOccupyLengthOptions,
} from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/consts";
import {
  EXPLANATION_POSITION,
  IOwFFormFieldSchema,
  OwFFormFieldValueType,
} from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import {
  getAlreadyOpenStoreOpeningDateOptions,
  getStoreOpeningDateOptionsWithStringValue,
  getDefaultStoreOpeningDate,
  getSoftCreditCheckFlowRequiredStoreFieldKeys,
  getNMIFlowRequiredStoreFieldKeys,
} from "@faire/retailer/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { useOpenWithFaireStore } from "@faire/retailer/stores/domain/OpenWithFaireStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { SquareMeterSuffix } from "@faire/retailer/views/SignUp/components/OpenWithFaire/components/styled";

import { getVisibleFieldKeys } from "./helpers";
import { useIsSoftCreditCheckApplication } from "./useIsSoftCreditCheckApplication";
import { useOwFIDVTierLogic } from "./useOwFIDVTierLogic";
import { useValidatePartialFormData } from "./useValidatePartialFormData";
import { useIsNMIApplication } from "./useIsNMIApplication";

interface IUseStoreInfo {
  formData: IRetailerLaunchWithFaireApplication;
  setValue: (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ) => void;
  isStoreAddressFormVisible: boolean;
  toggleStoreAddressForm: () => void;
  isSignUp?: boolean;
}

export const useStoreInfo = ({
  formData,
  setValue,
  isStoreAddressFormVisible,
  toggleStoreAddressForm,
  isSignUp,
}: IUseStoreInfo) => {
  const { strictLocalize } = useStrictLocalization();
  const { isRetailerStartedThisYear } = useOpenWithFaireStore([
    "isRetailerStartedThisYear",
  ]);
  const { retailer, retailerCountry, isEuropeNonUk, currency } = useRetailerStore([
    "retailer",
    "retailerCountry",
    "isEuropeNonUk",
    "currency",
  ]);
  const { validatePartialFormData } = useValidatePartialFormData();
  const { isAnyTierActive } = useOwFIDVTierLogic();
  const { isNMIApplication } = useIsNMIApplication();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  // computed values
  const isRetailerOther =
    retailer?.business_category === IRetailer.BusinessCategory.BUSINESS_OTHER;
  const retailerBusinessType = isRetailerOther
    ? formData.best_store_type ?? IRetailer.BusinessCategory.ONLINE_ONLY
    : retailer?.business_category;
  const isBrickAndMortar =
    retailerBusinessType === IRetailer.BusinessCategory.BRICK_AND_MORTAR;
  const hasSignedLease =
    formData.lease_signed ===
    IRetailerLaunchWithFaireApplication.LeaseSigned.YES;
  const ownsOwnStore =
    formData.lease_signed ===
    IRetailerLaunchWithFaireApplication.LeaseSigned.OWNED;
  const showDetailedStoreQuestions = hasSignedLease || ownsOwnStore;
  const hasNoLeaseSigned =
    formData.lease_signed ===
    IRetailerLaunchWithFaireApplication.LeaseSigned.NO;
  const isOnlineOnly =
    retailerBusinessType === IRetailer.BusinessCategory.ONLINE_ONLY;
  const isPopUp = retailerBusinessType === IRetailer.BusinessCategory.POP_UP;
  const shouldShowWebsiteSocialMediaCombinedField = !retailer?.google_place_id;

  const param = {
    country: retailerCountry,
  };
  const { data: marketingAmountRangesResponse } =
    useMarketingAmountRangesQuery(param);
  const marketingAmountRanges =
    marketingAmountRangesResponse?.marketing_amount_ranges;

  const squareFootageOrMeterageField = isEuropeNonUk
    ? "store_square_meterage"
    : "store_square_footage";

  const getBaseStoreFieldKeys = (): OwFFormFieldKey[] => {
    const keys: OwFFormFieldKey[] =
      isAnyTierActive || isSignUp ? [] : [...DefaultRequiredStoreFieldKeys];

    if (isRetailerOther) {
      keys.push("best_store_type");
    }

    if (
      (isAnyTierActive || isSignUp) &&
      getDefaultStoreOpeningDate(
        strictLocalize,
        retailer?.launch_with_faire?.store_opening_month,
        retailer?.reported_creation_time,
      ) === undefined
    ) {
      keys.push("opening_range");
    }

    return keys;
  };

  const requiredStoreFieldKeys: OwFFormFieldKey[] = (() => {
    if (isNMIApplication) {
      const fieldsToAdd: OwFFormFieldKey[] = (isBrickAndMortar || isPopUp) ? [squareFootageOrMeterageField] : [];

      return getNMIFlowRequiredStoreFieldKeys(
        retailerBusinessType,
        formData.lease_signed,
        fieldsToAdd,
      );
    }
    if (isSoftCreditCheckApplication) {
      return getSoftCreditCheckFlowRequiredStoreFieldKeys(
        isRetailerOther,
        retailerBusinessType,
        showDetailedStoreQuestions,
        formData
      );
    }

    let keys = getBaseStoreFieldKeys();

    switch (retailerBusinessType) {
      case IRetailer.BusinessCategory.BRICK_AND_MORTAR:
        keys.push("lease_signed");
        if (isAnyTierActive) {
          if (showDetailedStoreQuestions) {
            const fieldsToAdd = (
              [
                "store_address",
                hasSignedLease ? "lease_length" : null,
                "monthly_rent",
                squareFootageOrMeterageField,
              ] as Array<OwFFormFieldKey>
            ).filter(isNotNull);

            keys = keys.concat(fieldsToAdd);
          }
        } else {
          if (hasSignedLease) {
            keys.push("lease_length");
          }

          if (showDetailedStoreQuestions) {
            keys = keys.concat([
              "monthly_rent",
              "store_address",
              squareFootageOrMeterageField,
            ]);
          }
        }

        break;
      case IRetailer.BusinessCategory.POP_UP:
        if (isAnyTierActive) {
          keys = keys.concat([
            "popup_location_checkboxes",
            "popup_locations",
            squareFootageOrMeterageField,
            "popup_hours_open",
          ]);
        } else {
          keys = keys.concat([
            "popup_location_checkboxes",
            "popup_locations",
            "popup_lease",
            "popup_months_open",
            squareFootageOrMeterageField,
            "popup_days_per_week",
            "popup_hours_open",
          ]);

          if (
            formData.popup_lease ===
            IRetailerLaunchWithFaireApplication.PopUpLease.LEASE_NOT_REQUIRED
          ) {
            keys.push("popup_cost");
          } else if (formData.popup_lease) {
            keys.push("monthly_rent");
          }
        }

        break;
      case IRetailer.BusinessCategory.ONLINE_ONLY:
        if (!isAnyTierActive) {
          keys = keys.concat(["online_has_store_website"]);
          if (
            formData.online_has_store_website ===
            IRetailerLaunchWithFaireApplication.HasStoreWebsite.HAS_WEBSITE
          ) {
            keys.push("website_url");
          } else if (
            formData.online_has_store_website ===
            IRetailerLaunchWithFaireApplication.HasStoreWebsite.NO_WEBSITE
          ) {
            keys.push("online_website_proof_documents");
          }
        }

        if (isAnyTierActive) {
          keys = keys.concat(["marketing_amount", "store_hours_commitment"]);
        } else {
          keys = keys.concat([
            "customer_acquisition_plan",
            "marketing_amount",
            "store_hours_commitment",
          ]);
        }

        break;
      default:
        break;
    }
    return keys;
  })();

  const visibleStoreInfoKeys: OwFFormFieldKey[] = (() => {
    const visibleKeys = isAnyTierActive
      ? [...requiredStoreFieldKeys]
      : getVisibleFieldKeys(
          [
            "best_store_type",
            "store_name",
            "opening_range",
            "lease_signed",
            "online_has_store_website",
            "website_url",
            "online_website_proof_documents",
            "customer_acquisition_plan",
            "marketing_amount",
            "store_hours_commitment",
            "popup_location_checkboxes",
            "popup_locations",
            "popup_lease",
            "lease_length",
            "monthly_rent",
            "popup_cost",
            "store_address",
            "popup_months_open",
            squareFootageOrMeterageField,
            "popup_days_per_week",
            "popup_hours_open",
          ],
          requiredStoreFieldKeys
        );

    if (!isNMIApplication && isSoftCreditCheckApplication) {
      if (
        (isBrickAndMortar || isPopUp) &&
        shouldShowWebsiteSocialMediaCombinedField
      ) {
        return [...visibleKeys, "website_url_social_media_combined"];
      } else if (isOnlineOnly) {
        return [...visibleKeys, "social_media"];
      }
    }

    return (isOnlineOnly || isAnyTierActive) && !isSoftCreditCheckApplication
      ? [...visibleKeys, "social_media"]
      : visibleKeys;
  })();

  const getStoreInfoErrors = () =>
    validatePartialFormData({
      formData,
      keysToValidate: requiredStoreFieldKeys,
      isStoreAddressFormVisible,
    });

  const monthlyRentFieldSchema: IOwFFormFieldSchema | null = (() => {
    const baseSchema: IOwFFormFieldSchema = {
      fieldType: OwFFormFieldType.MONEY,
      fieldName: "monthly_rent",
      required: true,
    };

    if (isBrickAndMortar) {
      return {
        ...baseSchema,
        ...getBrickAndMortarMonthlyRentText(
          formData.lease_signed === "YES",
          currency,
          strictLocalize
        ),
      };
    }

    if (isPopUp) {
      return {
        ...baseSchema,
        // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
        ...getNonBMMonthlyRentText(strictLocalize)[formData.popup_lease ?? ""],
      };
    }

    return null;
  })();

  const getNotOpenOpeningRangeLabel = () => {
    if (isOnlineOnly) {
      return strictLocalize({
        defaultMessage: "When do you plan to launch your store?",
        description: { text: "Estimated time open the store" },
      });
    }

    return strictLocalize({
      defaultMessage: "When do you plan to open?",
      description: { text: "Estimated time open the store" },
    });
  };

  const getNewlyOpenOpeningRangeLabel = () => {
    if (isOnlineOnly) {
      return strictLocalize({
        defaultMessage: "When did you launch your store?",
        description: {
          text: "Asking retailer to estimate when they launched their store",
          img: "https://cdn.faire.com/fastly/e36cab2283be5d34e55966445189c52d72f2f54c8807c8bdde8d39e56ce57b41.png",
        },
      });
    }

    return strictLocalize({
      defaultMessage: "When did you open your store?",
      description: {
        text: "Asking retailer to estimate when they opened their store",
        img: "https://cdn.faire.com/fastly/f10d7bf9686069ff5f929efed488f38224f9c16214518d20d6f0251e1e29e5f9.png",
      },
    });
  };

  const onlineStoreHasWebsiteOptions = useOnlineHasStoreWebsiteOptions();

  const leaseSignedNoteText = strictLocalize({
    defaultMessage:
      "We recommend securing a store space before submitting your application. If you do submit your application now, we may contact you for additional information.",
  });

  const leaseLengthText = () => {
    if (isAnyTierActive) {
      return strictLocalize({
        defaultMessage:
          "How long is the length of the lease for your storefront?",
        description: {
          text: "Question in an application for Open with Faire",
        },
      });
    }

    return strictLocalize({
      defaultMessage: "What’s the length of the lease?",
      description: {
        text: "Question in an application for Open with Faire",
      },
    });
  };

  const storeSquareFootageText: string = (() => {
    switch (retailerBusinessType) {
      case IRetailer.BusinessCategory.POP_UP:
        return strictLocalize({
          defaultMessage:
            "What’s the approximate square footage of your pop-up location(s)?",
          description: {
            text: "Open with Faire application question",
            img: "https://cdn.faire.com/fastly/d6366b7937d99a053c47fc8c560b147379a25a7a1a41d105618329ac2c459875.png",
          },
        });
      default:
        return isNMIApplication ? strictLocalize({
          defaultMessage: "What’s the approximate square footage of your storefront?",
          description: {
            text: "Question in Open with Faire application form",
            img: "https://cdn.faire.com/fastly/9e35c10a140ff8cf79b2970eda913c36e35e1d6700abbb42206b9eb17059b91a.png"
          },
        }) : strictLocalize({
          defaultMessage: "What’s the approximate square footage?",
          description: {
            text: "Question in an application for Open with Faire",
          },
        });
    }
  })();

  const storeSquareMeterageText = () => {
    if (!isBrickAndMortar) {
      return strictLocalize({
        defaultMessage:
          "What’s the approximate square meters of your pop-up location(s)?",
        description: {
          text: "Open with Faire application question",
        },
      });
    }

    return isNMIApplication ? strictLocalize({
      defaultMessage: "What’s the approximate square meters of your storefront?",
      description: {
        text: "Question in Open with Faire application form",
        img: "https://cdn.faire.com/fastly/f8af056b74f3629458e625a1681e7ffbc0d7bcca69b7874be7a9d7d25e9d1fbb.png"
      },
    }) : strictLocalize({
      defaultMessage: "What’s the approximate square meters?",
      description: {
        text: "Open with Faire application question",
      },
    });
  };

  const storeInfoFields: IOwFFormFieldSchema[] = visibleStoreInfoKeys
    // eslint-disable-next-line complexity
    .map<IOwFFormFieldSchema | null>((key) => {
      switch (key) {
        case "opening_range":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "opening_range",
            labelText: isRetailerStartedThisYear
              ? getNewlyOpenOpeningRangeLabel()
              : getNotOpenOpeningRangeLabel(),
            options: isRetailerStartedThisYear
              ? getAlreadyOpenStoreOpeningDateOptions(strictLocalize)
              : getStoreOpeningDateOptionsWithStringValue(strictLocalize, true),
            required: true,
          };
        case "lease_signed":
          return {
            fieldType: OwFFormFieldType.RADIO,
            fieldName: "lease_signed",
            labelText: isNMIApplication ? strictLocalize({
              defaultMessage: "Have you signed a lease for your retail space?",
              description: {
                text: "Question in Open with Faire application form",
                img: "https://cdn.faire.com/fastly/9e35c10a140ff8cf79b2970eda913c36e35e1d6700abbb42206b9eb17059b91a.png"
              },
            }) : strictLocalize({
              defaultMessage: "Have you signed a lease for your store yet?",
            }),
            note:
              isBrickAndMortar && hasNoLeaseSigned
                ? leaseSignedNoteText
                : undefined,
            options: LeaseSignedOptions(strictLocalize),
            required: true,
          };
        case "online_has_store_website":
          return {
            fieldType: OwFFormFieldType.RADIO,
            fieldName: "online_has_store_website",
            labelText: strictLocalize({
              defaultMessage:
                "To verify your online business, we need information about your store website. Do you have a live website link you can provide?",
            }),
            options: onlineStoreHasWebsiteOptions,
            required: true,
          };
        case "website_url":
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "website_url",
            labelText: strictLocalize({
              defaultMessage: "Live website URL",
              description: {
                text: "Form field asking for retailer's live website link",
              },
            }),
            required: true,
          };
        case "online_website_proof_documents":
          return {
            fieldType: OwFFormFieldType.FILE,
            fieldName: "online_website_proof_documents",
            labelText: strictLocalize({
              defaultMessage:
                "Please upload one of the following documents about your store website",
            }),
            subTitleList: [
              strictLocalize({
                defaultMessage: "Proof of store website domain registration",
                description: {
                  text: "Used to verify that an online-only retailer's website is legitimate",
                },
              }),
              strictLocalize({
                defaultMessage:
                  "Screenshot of store website under construction",
                description: {
                  text: "Used to verify that an online-only retailer's website is legitimate",
                },
              }),
              strictLocalize({
                defaultMessage: "Wireframes of store website",
                description: {
                  text: "Used to verify that an online-only retailer's website is legitimate",
                },
              }),
            ],
            required: true,
          };
        case "social_media":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "social_media",
            labelText: strictLocalize({
              defaultMessage: "Share any social media links for your store",
              description: {
                text: "Ask retailers to provide the social media link for their store",
              },
            }),
            explanation: strictLocalize({
              defaultMessage: "E.g. instagram.com/generalstore",
              description: {
                text: "Explanation text for a form field asking for retailer's social media links",
              },
            }),
            explanationPosition: EXPLANATION_POSITION.TOP,
            required: false,
          };
        case "website_url_social_media_combined":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "website_url_social_media_combined",
            labelText: strictLocalize({
              defaultMessage: "Share links to your website or social media",
              description: {
                text: "Ask retailers to provide the website or social media link for their store",
                img: "https://cdn.faire.com/fastly/9bbaa7e93ee705f43d768ae535c143a955791e8341ce4984c6481b60d4a709b2.png",
              },
            }),
            explanation: strictLocalize({
              defaultMessage: "E.g. instagram.com/generalstore",
              description: {
                text: "Explanation text for a form field asking for retailer's social media links",
              },
            }),
            explanationPosition: EXPLANATION_POSITION.TOP,
            required: false,
          };
        case "customer_acquisition_plan":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "customer_acquisition_plan",
            labelText: strictLocalize({
              defaultMessage: "How do you plan to acquire your customers?",
              description: {
                text: "Asking retailers their plan to acquire customer",
              },
            }),
            explanation: strictLocalize({
              defaultMessage:
                "E.g. social media, paid advertising, email marketing, etc.",
              description: {
                text: "Explanation text for a form field asking for retailer's plan to acquire customers",
              },
            }),
            explanationPosition: EXPLANATION_POSITION.TOP,
            required: true,
          };
        case "marketing_amount":
          return {
            fieldType: OwFFormFieldType.MONEY_RANGE_DROPDOWN,
            fieldName: "marketing_amount",
            labelText: strictLocalize({
              defaultMessage:
                "How much do you plan to spend on marketing per month?",
              description: {
                text: "Asking retailers their budget on marketing",
              },
            }),
            moneyRangeOptions: marketingAmountRanges,
            required: true,
          };
        case "store_hours_commitment":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "store_hours_commitment",
            labelText: strictLocalize({
              defaultMessage:
                "How much time do you plan to spend on your store?",
              description: {
                text: "Asking retailers how much time they are willing to devote to their stores",
              },
            }),
            options: OnlineStoreCommitment(strictLocalize),
            required: true,
          };
        case "popup_location_checkboxes":
          return {
            fieldType: OwFFormFieldType.CHECKBOX,
            fieldName: "popup_location_checkboxes",
            labelText: strictLocalize({
              defaultMessage: "Where do you plan to set up your pop-up?",
              description: {
                text: "Question in Open with Faire application, pop-up means a pop-up shop",
              },
            }),
            checkBoxValues: [
              {
                label: strictLocalize({
                  defaultMessage: "Fairs, markets, or events",
                  description: { text: "Example of pop-up shop locations" },
                }),
                checked: formData["popup_location_events"] ?? false,
                onChange: () => {
                  setValue("popup_location_checkboxes", "touched");
                  setValue(
                    "popup_location_events",
                    !formData["popup_location_events"]
                  );
                },
              },
              {
                label: strictLocalize({
                  defaultMessage:
                    "Within a permanent brick-and-mortar location",
                  description: { text: "Example of pop-up shop location" },
                }),
                checked: formData["popup_location_brick_and_mortar"] ?? false,
                onChange: () => {
                  setValue("popup_location_checkboxes", "touched");
                  setValue(
                    "popup_location_brick_and_mortar",
                    !formData["popup_location_brick_and_mortar"]
                  );
                },
              },
            ],
            required: true,
          };
        case "popup_locations":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "popup_locations",
            labelText: strictLocalize({
              defaultMessage:
                "Please list the names and addresses of your pop-up locations.",
              description: {
                text: "Question in Open with Faire application",
              },
            }),
            required: true,
          };
        case "popup_lease":
          return {
            fieldType: OwFFormFieldType.RADIO,
            fieldName: "popup_lease",
            labelText: strictLocalize({
              defaultMessage:
                "Have you signed a lease for any of your pop-up location(s)?",
              description: {
                text: "Question in Open with Faire application",
              },
            }),
            options: PopUpLeaseOptions(strictLocalize),
            required: true,
          };
        case "lease_length":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "lease_length",
            labelText: leaseLengthText(),
            options: LeaseLengthOptions(strictLocalize),
            required: true,
          };
        case "monthly_rent":
          return monthlyRentFieldSchema;
        case "popup_cost":
          return {
            fieldType: OwFFormFieldType.TEXTAREA,
            fieldName: "popup_cost",
            ...getNonBMMonthlyRentText(strictLocalize)[
              IRetailerLaunchWithFaireApplication.PopUpLease.LEASE_NOT_REQUIRED
            ],
            required: true,
          };
        case "store_address":
          return {
            fieldType: isStoreAddressFormVisible
              ? OwFFormFieldType.ADDRESS_FORM
              : OwFFormFieldType.STORE_ADDRESS_AUTOCOMPLETE,
            fieldName: "store_address",
            labelText: strictLocalize({
              defaultMessage: "What’s your store’s address?",
              description: {
                text: "Question in an application for Open with Faire",
              },
            }),
            address: formData.store_address,
            required: showDetailedStoreQuestions,
            onChange: (value: OwFFormFieldValueType) => {
              if (isStoreAddressFormVisible) {
                setValue("store_address_google_place_id", undefined);
              }
              setValue(key, value);
            },
            onSelectAddress: (value) => {
              setValue("store_address_google_place_id", value);
            },
            toggleAddressForm: toggleStoreAddressForm,
          };
        case "popup_months_open":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "popup_months_open",
            labelText: strictLocalize({
              defaultMessage:
                "In total, how long do you plan to occupy your pop-up location(s)?",
              description: {
                text: "Question in an application for Open with Faire",
              },
            }),
            options: PopUpOccupyLengthOptions(strictLocalize),
            required: true,
          };
        case "store_square_footage":
          return {
            fieldType: OwFFormFieldType.NUMBER,
            fieldName: "store_square_footage",
            labelText: storeSquareFootageText,
            inputSuffix: "sq. ft.",
            required: true,
          };
        case "store_square_meterage":
          return {
            fieldType: OwFFormFieldType.NUMBER,
            fieldName: "store_square_meterage",
            labelText: storeSquareMeterageText(),
            inputSuffix: SquareMeterSuffix,
            required: true,
          };
        case "popup_days_per_week":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "popup_days_per_week",
            labelText: isNMIApplication ? strictLocalize({
              defaultMessage:
                "How many days a week will your pop-up be open?",
              description: {
                text: "Open with Faire application question. Pop-up is store type",
                img: "https://cdn.faire.com/fastly/d6366b7937d99a053c47fc8c560b147379a25a7a1a41d105618329ac2c459875.png",
              },
            }) : strictLocalize({
              defaultMessage:
                "Generally, how many days a week will your pop-up be open?",
              description: {
                text: "Open with Faire application question. Pop-up is store type",
              },
            }),
            options: PopUpDaysPerWeek(strictLocalize),
            required: true,
          };
        case "popup_hours_open":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "popup_hours_open",
            labelText: isNMIApplication ? strictLocalize({
              defaultMessage:
                "How many hours a day will your pop-up be open?",
              description: {
                text: "Open with Faire application question. Pop-up is store type",
                img: "https://cdn.faire.com/fastly/d6366b7937d99a053c47fc8c560b147379a25a7a1a41d105618329ac2c459875.png",
              },
            }) : strictLocalize({
              defaultMessage:
                "Generally, how many hours a day will your pop-up be open?",
              description: {
                text: "Open with Faire application question. Pop-up is store type",
              },
            }),
            options: PopUpHoursOptions(strictLocalize),
            required: true,
          };
        case "best_store_type":
          return {
            fieldType: OwFFormFieldType.RADIO,
            fieldName: "best_store_type",
            labelText: strictLocalize({
              defaultMessage:
                "What type of store best describes your business?",
              description: {
                text: "Asking retailers to select an business type option that best describes their store. E.g. brick-and-mortar",
              },
            }),
            options: BestStoreType(strictLocalize),
            required: true,
          };
        default:
        case "store_name":
          return {
            fieldType: OwFFormFieldType.INPUT,
            fieldName: "store_name",
            labelText: strictLocalize({
              defaultMessage: "What's the name of your store?",
              description: { text: "Open with Faire application question" },
            }),
            required: true,
          };
      }
    })
    .filter(isNotNull);

  return {
    visibleStoreInfoKeys,
    getStoreInfoErrors,
    storeInfoFields,
  };
};
